import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/apiClient';

interface TriageRow {
  severity: string;
  triageCount: number;
  closeCount: number;
  mtttSeconds: number | null;
  mttcSeconds: number | null;
}
interface TriageResponse { metrics: TriageRow[] }

function fmtSeconds(s: number | null): string | null {
  if (s === null || !Number.isFinite(s)) return null;
  if (s < 60)        return `${Math.round(s)}s`;
  const mins = s / 60;
  if (mins < 60)     return `${Math.round(mins)}m`;
  const hours = mins / 60;
  if (hours < 24)    return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}

/**
 * Fetches the per-severity Sentinel KQL response-time metrics and reduces
 * them to a single count-weighted aggregate MTTD + MTTR pair (formatted as
 * the short "5.1h" style used on the top KPI cards). Returns null fields
 * when the endpoint is unreachable so callers can fall back to local data.
 *
 * Same source of truth as the MttrWidget - keeps the top KPI cards and the
 * detailed Response-time table consistent.
 */
export function useTriageSummary(customerId: string, range: '24h' | '7d' | '30d' | '60d' | '90d' | 'all' = '30d'): {
  mttd: string | null;
  mttr: string | null;
  loading: boolean;
} {
  const [mttd, setMttd] = useState<string | null>(null);
  const [mttr, setMttr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!customerId || customerId === 'default') { setMttd(null); setMttr(null); return; }
    let cancelled = false;
    setLoading(true);
    apiFetch<TriageResponse>(`/sla/triage-metrics?customerId=${encodeURIComponent(customerId)}&range=${range}`)
      .then((d) => {
        if (cancelled) return;
        let triageCountTotal = 0, triageWeighted = 0, closeCountTotal = 0, closeWeighted = 0;
        for (const m of d.metrics ?? []) {
          if (m.mtttSeconds !== null && m.triageCount > 0) {
            triageCountTotal += m.triageCount;
            triageWeighted += m.mtttSeconds * m.triageCount;
          }
          if (m.mttcSeconds !== null && m.closeCount > 0) {
            closeCountTotal += m.closeCount;
            closeWeighted += m.mttcSeconds * m.closeCount;
          }
        }
        setMttd(triageCountTotal > 0 ? fmtSeconds(triageWeighted / triageCountTotal) : null);
        setMttr(closeCountTotal > 0 ? fmtSeconds(closeWeighted / closeCountTotal) : null);
      })
      .catch(() => { if (!cancelled) { setMttd(null); setMttr(null); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customerId, range]);

  return { mttd, mttr, loading };
}
